import { ScatterplotLayer } from '@deck.gl/layers';
import type { LayerState } from '@vitals/shared';
import { LAYER_MNR } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { createCometMotion } from './cometMotion.js';

const DOT_RADIUS = 5; // px

interface MnrDatum {
  position: [number, number];
  color: [number, number, number];
  destination?: string;
  nextStop?: string;
  routeId: string;
}

// Own motion instance: isolated state so it never corrupts the subway or LIRR
// comets' lastFrameMs or displayed-positions maps (and vice versa).
const motion = createCometMotion();

/**
 * Build a ScatterplotLayer of branch-colored Metro-North train dots. Color is
 * baked into `props.color` by the server (one color per MNR line). Trains
 * glide along their schedule path via the shared cometMotion core (own
 * instance). Returns null when state is absent, down, or has no features —
 * matches the convention used by lirrLayer and other renderers.
 */
export function buildMnrCometLayer(
  state: LayerState,
  nowSec: number,
): ScatterplotLayer<MnrDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const data: MnrDatum[] = motion.step(state, nowSec).map((s) => ({
    position: s.position,
    color: s.props.color as [number, number, number],
    destination: s.props.destination as string | undefined,
    nextStop: s.props.nextStop as string | undefined,
    routeId: s.props.routeId as string,
  }));

  return new ScatterplotLayer<MnrDatum>({
    id: LAYER_MNR,
    data,
    getPosition: (d) => d.position,
    getFillColor: (d) => d.color,
    getRadius: DOT_RADIUS,
    radiusUnits: 'pixels',
    stroked: true,
    getLineColor: [255, 255, 255],
    lineWidthUnits: 'pixels',
    getLineWidth: 1,
    pickable: true,
  });
}

export const mnrRenderer: RendererDef = {
  layerId: LAYER_MNR,
  build: (state, nowSec) => buildMnrCometLayer(state, nowSec),
};
